import React, { useState } from "react";
import styled from "styled-components";
import { RunRecord, RunStatus } from "../mockData";
import { Mono } from "../ui";
import { RunSummary, RunTone, runDisplay, useRuns } from "../service";
import RunDetailModal from "../RunDetailModal";

/** Map a canonical Manager Job (with the bounded MI `metadata`) to the display row the table
 *  renders. Status derives from the job status, refined by the precise MI outcome so a deliberate
 *  no-write case (stale) reads distinctly from a clean completion. */
function summaryToRow(r: RunSummary): RunRecord {
  const m = r.metadata;
  // Coarse status kept as a tone fallback; the precise label comes from `outcome` via runDisplay
  // (so stale/planned never read as an ordinary completed write).
  let status: RunStatus;
  if (r.status === "error") {
    status = "failed";
  } else if (r.status === "warning") {
    status = "warning";
  } else if (
    r.status === "running" ||
    r.status === "retrying" ||
    r.status === "waiting"
  ) {
    status = "running";
  } else {
    status = "ok";
  }
  return {
    id: r.jobId,
    date: r.startedAt ?? "",
    startedAt: r.startedAt,
    trigger: m?.trigger ?? "manual",
    source: "",
    total: m?.targeted ?? 0,
    changed: m?.changed ?? 0,
    unchanged: m?.unchanged ?? 0,
    warnings: m?.warnings ?? 0,
    failures: m?.failed ?? 0,
    status,
    outcome: m?.outcome,
    revision: m?.revision,
    planRevision: m?.planRevision,
    failureSummary: m?.failureSummary,
  };
}

/**
 * Profile → Runs tab. Jobs-style run history — date, trigger, source, totals, changed/unchanged,
 * warnings, failures, status. Mirrors what the existing Iconik Manager Jobs surface reports.
 */

const Trigger = styled.span<{ $scheduled: boolean }>`
  justify-self: start;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
  color: ${(p) =>
    p.$scheduled ? "var(--miAccentBright)" : "var(--miInkSoft)"};
  background: ${(p) =>
    p.$scheduled ? "var(--miAccentSoft)" : "var(--miRaised)"};
  border: 1px solid
    ${(p) => (p.$scheduled ? "var(--miAccentLine)" : "var(--miLine)")};
`;

const TONE_VAR: Record<RunTone, string> = {
  ok: "var(--miOk)",
  warn: "var(--miWarn)",
  danger: "var(--miDanger)",
  running: "var(--miAccentBright)",
  info: "var(--miInkSoft)",
};

const StatusCell = styled.span<{ $tone: string }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: ${(p) => p.$tone};

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => p.$tone};
  }
`;

const Empty = styled.div`
  padding: 26px 20px;
  color: var(--miInkFaint);
  font-size: 14px;
  text-align: center;
`;

/** One run as a compact card (sidebar-friendly; the wide 9-column table doesn't fit a secondary
 *  column). Keeps the full projection + outcome semantics — status label from runDisplay, the key
 *  counts, and a click-through to the same detail modal. */
const RunItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid var(--miLine);
  border-radius: 0;
  padding: 10px 2px;
  cursor: pointer;

  &:first-child {
    padding-top: 2px;
  }
  &:last-child {
    border-bottom: none;
    padding-bottom: 2px;
  }
  &:hover {
    background: var(--miRaised);
  }
`;

const RunTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const RunMeta = styled.div`
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: var(--miInkFaint);

  b {
    color: var(--miInkSoft);
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }
`;

function RunItemCard({
  run,
  onOpen,
}: {
  run: RunRecord;
  onOpen: (r: RunRecord) => void;
}) {
  const d = runDisplay({
    status: run.status,
    outcome: run.outcome,
    changed: run.changed,
  });
  return (
    <RunItem onClick={() => onOpen(run)}>
      <RunTop>
        <StatusCell $tone={TONE_VAR[d.tone]}>{d.label}</StatusCell>
        <Trigger $scheduled={run.trigger === "scheduled"}>
          {run.trigger}
        </Trigger>
      </RunTop>
      <RunMeta>
        <Mono>{run.date}</Mono>
        <span>
          <b>{run.changed}</b> changed
        </span>
        <span>
          <b>{run.unchanged}</b> unchanged
        </span>
        {run.failures > 0 && (
          <span style={{ color: "var(--miDanger)" }}>
            <b style={{ color: "var(--miDanger)" }}>{run.failures}</b> failed
          </span>
        )}
      </RunMeta>
    </RunItem>
  );
}

export default function RunsPanel({ id }: { id: string }) {
  const runsQuery = useRuns(id);
  const runs: RunRecord[] = (runsQuery.data ?? []).map(summaryToRow);
  const [selected, setSelected] = useState<RunRecord | null>(null);

  return (
    <div>
      {selected && (
        <RunDetailModal run={selected} onClose={() => setSelected(null)} />
      )}
      {runsQuery.isLoading ? (
        <Empty>Loading runs…</Empty>
      ) : runs.length === 0 ? (
        <Empty>No runs yet — this workflow has not been run.</Empty>
      ) : (
        runs.map((run) => (
          <RunItemCard key={run.id} run={run} onOpen={setSelected} />
        ))
      )}
    </div>
  );
}
